/**
 * useDentalRole — returns true when the selected doctor is a dental specialty.
 *
 * Matching strategy (fixes false-positive bug):
 *  • DEPARTMENT  → exact match (case-insensitive, trimmed) against a known set.
 *  • SPECIALIZATION → substring match, but only with unambiguous dental stems.
 *    Removed: "implant" (cardiac/ortho collision), "rct" (ambiguous abbreviation).
 *
 * Caching:
 *  Module-level Map keyed by doctorId with 5-minute TTL.
 *  Prevents redundant sequential API calls on re-mount / StrictMode double-fire.
 */

import { useState, useEffect } from "react";
import { OnboardingAPI } from "../services/onboarding.service";
import { DoctorAPI } from "../services/doctor.service";

// ── DEPARTMENT — exact match (case-insensitive, trimmed) ─────────────
// Departments are typically standardised dropdown values, so exact
// matching eliminates cross-specialty collisions entirely.
const DENTAL_DEPARTMENTS = new Set([
  "dentistry",
  "dentist",
  "dental",
  "dental surgery",
  "orthodontics",
  "orthodontist",
  "endodontics",
  "endodontist",
  "periodontics",
  "periodontist",
  "prosthodontics",
  "prosthodontist",
  "oral surgery",
  "oral medicine",
  "oral pathology",
  "pedodontics",
  "pedodontist",
  "pediatric dentistry",
  "paediatric dentistry",
  "cosmetic dentistry",
  "implantology",
  "conservative dentistry",
  "community dentistry",
  "public health dentistry",
]);

// ── SPECIALIZATION — substring match (unambiguous stems only) ────────
// Removed: "implant" (matches "Pacemaker implantation", ortho implants),
//          "rct"     (ambiguous 3-letter abbreviation).
// Kept:    "implantolog" (unambiguous — dental implantology only).
const DENTAL_SPEC_STEMS = [
  // General
  "dentist", "dental",
  // Orthodontics
  "orthodont", "braces", "aligners", "invisalign",
  // Endodontics
  "endodont", "root canal",
  // Periodontics
  "periodont", "gum specialist",
  // Prosthodontics
  "prosthodont", "denture",
  // Oral surgery
  "oral surg", "oral surgeon", "wisdom tooth",
  // Cosmetic / paediatric
  "cosmetic dent", "pediatric dent", "paediatric dent", "pedodont",
  // Implantology / smile
  "implantolog", "smile makeover",
  // Colloquial
  "teeth doctor", "tooth doctor",
  // Degrees (unambiguous dental degrees)
  "bds", "dds", "dmd",
];

// ── Cache (module-level, survives re-mounts) ─────────────────────────
const _cache = new Map<string, { isDentist: boolean; ts: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function _getCached(key: string): boolean | null {
  const hit = _cache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.ts > CACHE_TTL_MS) {
    _cache.delete(key);
    return null;
  }
  return hit.isDentist;
}

function _setCache(key: string, isDentist: boolean) {
  _cache.set(key, { isDentist, ts: Date.now() });
}

export function clearDentalRoleCache() {
  _cache.clear();
}

// ── Matchers ─────────────────────────────────────────────────────────

function _deptMatch(dept: string | null | undefined): boolean {
  if (!dept) return false;
  return DENTAL_DEPARTMENTS.has(dept.trim().toLowerCase());
}

function _specMatch(spec: string | null | undefined): boolean {
  if (!spec) return false;
  const lower = spec.toLowerCase();
  return DENTAL_SPEC_STEMS.some((stem) => lower.includes(stem));
}

// ── Hook ─────────────────────────────────────────────────────────────

export function useDentalRole(doctorId?: string): { isDentist: boolean; loading: boolean } {
  const [isDentist, setIsDentist] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const resolvedId = doctorId || (await OnboardingAPI.getMe()).doctor_id;
        if (!resolvedId) {
          if (!cancelled) setIsDentist(false);
          return;
        }

        // Check cache first
        const cacheKey = resolvedId;
        const cached = _getCached(cacheKey);
        if (cached !== null) {
          if (!cancelled) setIsDentist(cached);
          return;
        }

        const doctor = await DoctorAPI.get(resolvedId);
        const dental = _deptMatch(doctor.department) || _specMatch(doctor.specialization);

        _setCache(cacheKey, dental);
        if (!cancelled) setIsDentist(dental);
      } catch (_err) {
        if (!cancelled) setIsDentist(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    check();
    return () => { cancelled = true; };
  }, [doctorId]);

  return { isDentist, loading };
}
